import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { Navigation } from 'app/core/models/navigation.types';
import { AuthService } from 'app/core/services/auth.service';
import { map, Observable, ReplaySubject, tap } from 'rxjs';

@Injectable({providedIn: 'root'})
export class NavigationService
{
    private _httpClient = inject(HttpClient);
    private _authService = inject(AuthService);
    private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation>
    {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data
     */
    get(): Observable<Navigation>
    {
        return this._httpClient.get<Navigation>('api/common/navigation').pipe(
            map((navigation) =>
            {
                const userRoles = this._authService.getUserRoles().map(r => r.toLowerCase());
                return {
                    compact   : this._filterByRoles(navigation.compact, userRoles),
                    default   : this._filterByRoles(navigation.default, userRoles),
                    futuristic: this._filterByRoles(navigation.futuristic, userRoles),
                    horizontal: this._filterByRoles(navigation.horizontal, userRoles),
                };
            }),
            tap((navigation) =>
            {
                this._navigation.next(navigation);
            }),
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private _filterByRoles(items: FuseNavigationItem[], userRoles: string[]): FuseNavigationItem[]
    {
        const result: FuseNavigationItem[] = [];

        for (const item of items ?? [])
        {
            if (!this._hasAccess(item, userRoles)) continue;

            if (item.children?.length)
            {
                const filteredChildren = this._filterByRoles(item.children, userRoles);
                if (filteredChildren.length === 0 && (item.type === 'group' || item.type === 'collapsable'))
                {
                    continue;
                }
                result.push({ ...item, children: filteredChildren });
            }
            else
            {
                result.push(item);
            }
        }

        return result;
    }

    private _hasAccess(item: FuseNavigationItem, userRoles: string[]): boolean
    {
        if (!item.roles || item.roles.length === 0) return true;
        return item.roles.some(role => userRoles.includes(role.toLowerCase()));
    }
}
